import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle2,
  Facebook,
  MessageSquare,
  User,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: "Telefon",
    value: "0740 245 376",
    link: "tel:+40740245376",
    color: "bg-green-100 text-green-600"
  },
  {
    icon: Mail,
    title: "Email",
    value: "auto.cora12@gmail.com",
    link: "mailto:auto.cora12@gmail.com",
    color: "bg-blue-100 text-blue-600"
  },
  {
    icon: MapPin,
    title: "Adresă",
    value: "Str. Nicolae Bălcescu Nr. 1, Falticeni, Romania",
    link: "https://www.google.com/maps/search/Aleea+Esplanadei+10,+Dolhasca,+Romania,+727170",
    color: "bg-red-100 text-red-600"
  },
  {
    icon: Clock,
    title: "Program",
    value: "Luni - Vineri\n08:00 - 17:00",
    link: null,
    color: "bg-amber-100 text-amber-600"
  }
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      toast.error("Te rugăm să completezi toate câmpurile");
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Mesajul a fost trimis cu succes!");
      
      // Open WhatsApp with pre-filled message
      const whatsappNumber = "40740245376";
      const message = `Bună ziua! Am o întrebare pentru școala AUTO CORA.

📋 *Detalii contact:*
👤 Nume: ${formData.name}
📧 Email: ${formData.email}
📞 Telefon: ${formData.phone}
📌 Subiect: ${formData.subject}

💬 *Mesaj:*
${formData.message}

Mulțumesc!`;
      
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
    } catch (err) {
      console.error("Error submitting contact:", err);
      toast.error("A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-white py-16 md:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mt-4 tracking-tight font-['Archivo']">
              Hai Să Vorbim!
            </h1>
            <p className="text-slate-600 mt-6 text-lg">
              Ai întrebări despre cursurile noastre? Suntem aici să te ajutăm. 
              Contactează-ne prin oricare dintre metodele de mai jos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card 
                  data-testid={`contact-card-${item.title.toLowerCase()}`}
                  className="h-full border border-slate-200 hover:border-blue-500/50 hover:shadow-lg transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                    {item.link ? (
                      <a 
                        href={item.link}
                        className="text-slate-600 hover:text-blue-600 transition-colors whitespace-pre-line"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-slate-600 whitespace-pre-line">{item.value}</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Contact Form & Map */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-900 font-['Archivo']">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    Trimite-ne un Mesaj
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Mesaj Trimis!</h3>
                      <p className="text-slate-600 mb-6">
                        Mulțumim pentru mesaj. Te vom contacta în curând.
                      </p>
                      <Button
                        data-testid="new-message-btn"
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({
                            name: "",
                            email: "",
                            phone: "",
                            subject: "",
                            message: ""
                          });
                        }}
                        variant="outline"
                        className="border-slate-300"
                      >
                        Trimite Alt Mesaj
                      </Button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-700">Nume complet</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Ion Popescu"
                            className="pl-10 h-12 border-slate-200"
                            data-testid="contact-name-input"
                            required
                          />
                        </div>
                      </div>

                      {/* Email & Phone */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-slate-700">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="email@exemplu.ro"
                              className="pl-10 h-12 border-slate-200"
                              data-testid="contact-email-input"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-slate-700">Telefon</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <Input
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="0700 000 000"
                              className="pl-10 h-12 border-slate-200"
                              data-testid="contact-phone-input"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-slate-700">Subiect</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="Despre ce vrei să discutăm?"
                          className="h-12 border-slate-200"
                          data-testid="contact-subject-input"
                          required
                        />
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-slate-700">Mesaj</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Scrie mesajul tău aici..."
                          className="min-h-[150px] border-slate-200"
                          data-testid="contact-message-input"
                          required
                        />
                      </div>

                      {/* Submit */}
                      <Button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Se trimite...
                          </>
                        ) : (
                          <>
                            Trimite Mesajul
                            <Send className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Map & Social */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Map */}
              <Card className="border border-slate-200 overflow-hidden">
                <div className="aspect-[4/3] relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d674.3251908878362!2d26.2980861!3d47.4645682!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47351bc9b2bf2e51%3A0xdcb2422ffc57d696!2sStrada%20Nicolae%20B%C4%83lcescu%201%2C%20725200%20F%C4%83lticeni!5e0!3m2!1sro!2sro!4v1772667244755!5m2!1sro!2sro"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Locația AUTO CORA"
                    className="absolute inset-0"
                  ></iframe>
                  <a 
                    href="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d674.3251908878362!2d26.2980861!3d47.4645682!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47351bc9b2bf2e51%3A0xdcb2422ffc57d696!2sStrada%20Nicolae%20B%C4%83lcescu%201%2C%20725200%20F%C4%83lticeni!5e0!3m2!1sro!2sro!4v1772667244755!5m2!1sro!2sro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-3 right-3 bg-white px-3 py-2 rounded-lg shadow-md text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-2"
                  >
                    <MapPin className="w-4 h-4" />
                    Deschide în Google Maps
                  </a>
                </div>
              </Card>

              {/* Social Media */}
              <Card className="border border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-4">Urmărește-ne</h3>
                  <a
                    href="https://www.facebook.com/AutoCora"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook-link"
                    className="flex items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <Facebook className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Facebook</p>
                      <p className="text-slate-600 text-sm">AUTO CORA - Școala de Șoferi</p>
                    </div>
                  </a>
                </CardContent>
              </Card>

              {/* Quick Contact */}
              <Card className="border border-slate-200 bg-slate-900 text-white">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Sună-ne Direct</h3>
                  <a
                    href="tel:+40700000000"
                    className="flex items-center gap-4 p-4 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                  >
                    <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-2xl">0700 000 000</p>
                      <p className="text-slate-400 text-sm">Disponibil L-V, 08:00-17:00</p>
                    </div>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
